import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { pathToSettingsRoleEdit } from '@/navigation/navigate';
import { roleService } from '@/services/role-service';
import type { Role } from '@/types/core/role';
import { pollForResource } from '@/utils/poll';
import type { UUID } from '@/utils/uuid';

import { roleMutationKeys, roleQueryKeys } from './query-keys';

/**
 * Hook to create a role.
 * Returns a react-query mutation that accepts `{ name: string }`.
 * Invalidates the roles cache to trigger a refetch.
 */
export function useCreateRole(tenantId?: UUID, tenantSlug?: string) {
  const qc = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationKey: roleMutationKeys.createRole(tenantId),
    mutationFn: async (payload: { name: string; description?: string }) => {
      if (!tenantId) throw new Error('Missing tenant id');

      const role = await roleService.createRole(tenantId, {
        name: payload.name,
        description: payload.description,
      } as Role);

      const projected = await pollForResource(() =>
        roleService.findRole(tenantId, role.roleId)
      );

      return projected ?? role;
    },
    // Deliberately un-annotated: react-query infers this from mutationFn, and a
    // hand-written type drifting from that inference is the bug this replaced.
    onSuccess: (role) => {
      qc.invalidateQueries({
        queryKey: roleQueryKeys.listByTenant(tenantId),
        refetchType: 'all',
      });

      if (tenantSlug && role.roleId) {
        navigate(pathToSettingsRoleEdit(tenantSlug, role.roleId));
      }
    },
  });
}
